import type { ColorAttribute } from '../helpers';
import { findOptionsByLabelIgnoringCase } from './casing';
import type { StoreOption, VariantMappingRepairStrategy } from './types';

const repairOptionPosition = (attribute: ColorAttribute, options: readonly StoreOption[], canRemove: boolean): ColorAttribute[] | null => {
    const candidates = findOptionsByLabelIgnoringCase(attribute.Label, options);
    if (candidates.length === 0 && canRemove) return [];
    return candidates.length === 1 ? [{ ...attribute, Id: candidates[0].optionID }] : null;
};

export const repairShopifyOptionPositions = (attributes: readonly ColorAttribute[], options: readonly StoreOption[]): ColorAttribute[] | null => {
    // Match only the imported subset: deleting an unimported Shopify option can shift every saved ID.
    const canRemove = options.length > 0 && options.length < attributes.length;
    const results = attributes.map((attribute) => repairOptionPosition(attribute, options, canRemove));
    if (!results.every((result) => result !== null)) return null;
    const repaired = results.flat();
    const removedImportedOption = repaired.length < attributes.length;
    // Full store coverage is required only when dropping an imported attribute, not for position shifts.
    const coversStoreOptions = options.every((option) => repaired.some((attribute) => attribute.Id === option.optionID));
    return removedImportedOption && !coversStoreOptions ? null : repaired;
};

export const shopifyOptionPositionsStrategy: VariantMappingRepairStrategy = {
    id: 'shopify-option-positions',
    isApplicable: ({ isShopify }) => isShopify,
    repair: (attributes, { options }) => repairShopifyOptionPositions(attributes, options)
};

export const shopifyOptionOrderStrategy: VariantMappingRepairStrategy = {
    id: 'shopify-option-order',
    isApplicable: ({ isShopify }) => isShopify,
    repair: (attributes, { options }) => {
        const reordered = repairShopifyOptionPositions(attributes, options);
        const originalIDs = new Set(attributes.map((attribute) => attribute.Id));
        // A swap is a permutation of the imported positions. Shifts/deletions remain the next strategy's responsibility.
        const isPermutation =
            reordered !== null &&
            reordered.length === attributes.length &&
            originalIDs.size === attributes.length &&
            new Set(reordered.map((attribute) => attribute.Id)).size === attributes.length &&
            reordered.every((attribute) => originalIDs.has(attribute.Id));
        return isPermutation ? reordered : [...attributes];
    }
};
